import { useCallback } from 'react';
import type { FeatureCollection, Geometry } from 'geojson';
import { getSupabase, isSupabaseConfigured } from '../client';
import { useCachedFetch } from './useCachedFetch';

/**
 * useCapas — capas INFORMATIVAS (overlay de referencia) del tenant actual.
 *
 * Lee `v_capas_geojson` (security_invoker → RLS tenant-scoped, legible por
 * cliente y staff) y agrupa las filas (una por feature) en `CapaRender[]`
 * (una por capa). La forma de `CapaRender` es estructuralmente idéntica a la
 * de @aosenuma/sig-viewer → se pasa directo a `MapaParcelas.capasInformativas`
 * sin import cruzado de tipos.
 *
 * Solo trae informativas: la vista parte de `capa_features`, y las capas
 * operativas (predios) no tienen features ahí.
 */

export interface CapaEstilo {
  color?: string;
  grosor?: number;
  opacidad?: number;
  relleno?: boolean;
}

export interface CapaRender {
  id: string;
  nombre: string;
  visible?: boolean;
  estilo?: CapaEstilo;
  data: FeatureCollection;
}

/** Fila de `v_capas_geojson` (una por feature de capa informativa). */
export interface CapaGeojsonRow {
  /** id del capa_feature (para CRUD por-feature). */
  id: string;
  capa_id: string;
  capa_nombre: string;
  tipo: string;
  subtipo: string | null;
  estilo: CapaEstilo | null;
  visible: boolean;
  feature_nombre: string | null;
  properties: Record<string, unknown> | null;
  geom_geojson: unknown;
}

/** Núcleo PURO: agrupa filas de la vista en capas listas para render. Testeable sin red. */
export function buildCapasRender(rows: CapaGeojsonRow[]): CapaRender[] {
  const byId = new Map<string, CapaRender>();
  for (const r of rows) {
    let capa = byId.get(r.capa_id);
    if (!capa) {
      capa = {
        id: r.capa_id,
        nombre: r.capa_nombre,
        visible: r.visible,
        estilo: r.estilo ?? undefined,
        data: { type: 'FeatureCollection', features: [] },
      };
      byId.set(r.capa_id, capa);
    }
    if (r.geom_geojson) {
      capa.data.features.push({
        type: 'Feature',
        id: r.id, // capa_feature id → CRUD por-feature
        geometry: r.geom_geojson as Geometry,
        properties: { feature_nombre: r.feature_nombre, ...(r.properties ?? {}) },
      });
    }
  }
  return [...byId.values()];
}

export function useCapas(): {
  data: CapaRender[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
} {
  const fetcher = useCallback(async (): Promise<CapaRender[]> => {
    if (!isSupabaseConfigured()) return [];
    const sb = getSupabase();
    const { data, error } = await sb
      .from('v_capas_geojson')
      .select('id, capa_id, capa_nombre, tipo, subtipo, estilo, visible, feature_nombre, properties, geom_geojson');
    if (error) throw error;
    return buildCapasRender((data ?? []) as CapaGeojsonRow[]);
  }, []);

  const { data, loading, error, refetch } = useCachedFetch<CapaRender[]>(
    'capas_informativas',
    fetcher,
    { ttlMs: 30_000 },
  );

  return { data: data ?? [], loading, error, refetch };
}
